import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Coffee, Gem, Truck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { Eyebrow, SectionTitle, GoldButton, OutlineButton, ArrowRight } from "@/components/shared";
import { Ornament } from "@/components/Ornament";
import { FreshToday } from "@/components/FreshToday";
import { ProductDialog } from "@/components/ProductDialog";
import { MarketplaceModule } from "@/components/Marketplace";
import { IMAGES } from "@/data/treasures";

const pillarIcons = [Coffee, Gem, Truck];

const HomePage = () => {
  const { t, lang } = useLang();
  const revealRef = useReveal([lang]);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const openProduct = (item) => { setSelected(item); setDialogOpen(true); };

  return (
    <div ref={revealRef}>
      {/* HERO — warm café & vintage ambience */}
      <section data-testid="home-hero" className="relative min-h-screen flex items-center overflow-hidden bg-espresso-deep grain-overlay">
        <div className="absolute inset-0 scale-105" style={{ backgroundImage: `url(${IMAGES.cafeWarm})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-espresso-deep via-espresso-deep/85 to-espresso-deep/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep/90 via-transparent to-espresso-deep/40" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full pt-28 grid lg:grid-cols-[1fr_auto] gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.1 }} className="max-w-2xl">
            <Eyebrow dark>{t.hero.eyebrow}</Eyebrow>
            <h1 className="font-serif text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-shadow-warm">{t.hero.title}</h1>
            <Ornament className="mt-6 justify-start opacity-80" width={220} />
            <p className="mt-6 text-cream/85 text-lg md:text-xl font-sans leading-relaxed max-w-lg">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <GoldButton as={Link} to="/brockenstube" data-testid="hero-cta-discover">
                {t.cta.discover} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </GoldButton>
              <OutlineButton as={Link} to="/kontakt" dark data-testid="hero-cta-contact">{t.cta.visit}</OutlineButton>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }} className="hidden lg:block">
            <img src={IMAGES.logo} alt="Mara Wunderkammer" className="w-64 xl:w-72 rounded-2xl ring-1 ring-gold/30 shadow-[0_30px_80px_rgba(0,0,0,0.5)] animate-float-slow" />
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gold/70">
          <span className="text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </motion.div>
      </section>

      {/* INTRO */}
      <section className="relative py-24 md:py-36 bg-cream grain-overlay overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div data-reveal><Eyebrow>{t.homeIntro.eyebrow}</Eyebrow></div>
          <div data-reveal data-reveal-delay="0.1"><SectionTitle>{t.homeIntro.title}</SectionTitle></div>
          <p data-reveal data-reveal-delay="0.2" className="mt-8 text-espresso/70 text-lg leading-relaxed font-sans">{t.homeIntro.body}</p>
          <div data-reveal data-reveal-delay="0.3"><Ornament className="mt-10" width={240} /></div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 md:py-28 bg-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16" data-reveal><SectionTitle>{t.pillars.title}</SectionTitle></div>
          <div className="grid md:grid-cols-3 gap-8">
            {t.pillars.items.map((p, i) => {
              const Icon = pillarIcons[i];
              const to = ["/cafe", "/brockenstube", "/dienstleistungen"][i];
              const img = [IMAGES.cafeWarm, IMAGES.brockenWide, IMAGES.servicesImage][i];
              return (
                <Link to={to} key={i} data-reveal data-reveal-delay={0.1 * i} data-testid={`pillar-${i}`}
                  className="group relative bg-cream rounded-2xl overflow-hidden border border-border hover:border-gold/60 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(43,27,18,0.15)] flex flex-col">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={img} alt={p.t} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep/70 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-4 w-12 h-12 rounded-full bg-espresso/90 backdrop-blur flex items-center justify-center ring-1 ring-gold/40">
                      <Icon className="w-5 h-5 text-gold" />
                    </div>
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <h3 className="font-serif text-2xl text-espresso mb-3">{p.t}</h3>
                    <p className="text-espresso/65 leading-relaxed font-sans text-sm flex-1">{p.d}</p>
                    <span className="mt-6 inline-flex items-center gap-2 self-start rounded-full border border-gold/50 px-5 py-2.5 text-gold-deep text-sm tracking-wide transition-all duration-300 group-hover:bg-gold group-hover:text-espresso-deep group-hover:shadow-[0_0_24px_rgba(200,169,126,0.5)]">
                      {p.btn} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* LIVE SHOP WINDOW */}
      <FreshToday onOpen={openProduct} />

      {/* SWISS MARKETPLACES */}
      <MarketplaceModule />

      {/* CTA BAND */}
      <section className="relative py-28 md:py-36 bg-espresso-deep grain-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1731909997813-f3061fe69686?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-espresso-deep/75" />
        <div className="relative max-w-3xl mx-auto px-6 text-center" data-reveal>
          <Ornament className="mb-8 opacity-80" width={220} />
          <SectionTitle dark>{t.services.title}</SectionTitle>
          <p className="mt-6 text-cream/75 text-lg font-sans leading-relaxed">{t.services.trust}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <GoldButton as={Link} to="/dienstleistungen" data-testid="cta-band-services">{t.cta.appointment}</GoldButton>
          </div>
        </div>
      </section>

      <ProductDialog item={selected} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
};

export default HomePage;
